import {
  OOS_RECOVERY_OPTIONS,
  TOosRecoveryOption,
} from '../../constants/out-of-stock';
import { FULFILLMENT_TYPE } from '../../constants/status';
import { FeatureSwitches } from '../../constants/feature-switches';
import { IFulfillmentGroup } from '../../models/order-details';
import { IAvailableFulfillmentOptionsResponse } from '../../models/requests/omm';

const hasOosRecoveryOption = (
  fulfillmentGroup: IFulfillmentGroup,
  option: TOosRecoveryOption,
) => Boolean(fulfillmentGroup.oosRecoveryOptions?.includes(option));

export const isBopisToBodfsEnabled = () =>
  Boolean(FeatureSwitches.isBOPISToBODFSEnabled);

// BODFS is behind a FE rollout switch and the OMS recovery option; STH only
// needs the OMS recovery option because it is the existing delivery fallback.
export const canConsiderBodfsOosRecovery = (
  fulfillmentGroup: IFulfillmentGroup,
) =>
  isBopisToBodfsEnabled() &&
  hasOosRecoveryOption(
    fulfillmentGroup,
    OOS_RECOVERY_OPTIONS.BODFS_DELIVERY_OPTION,
  );

export const canConsiderSthOosRecovery = (
  fulfillmentGroup: IFulfillmentGroup,
) =>
  hasOosRecoveryOption(
    fulfillmentGroup,
    OOS_RECOVERY_OPTIONS.STH_DELIVERY_OPTION,
  );

export const getAllowedOosDeliveryFulfillmentTypes = (
  fulfillmentGroup: IFulfillmentGroup,
): FULFILLMENT_TYPE[] => {
  const allowedFulfillmentTypes: FULFILLMENT_TYPE[] = [];

  if (canConsiderSthOosRecovery(fulfillmentGroup)) {
    allowedFulfillmentTypes.push(FULFILLMENT_TYPE.STH);
  }

  if (canConsiderBodfsOosRecovery(fulfillmentGroup)) {
    allowedFulfillmentTypes.push(FULFILLMENT_TYPE.BODFS);
  }

  return allowedFulfillmentTypes;
};

export const getEligibleOosDeliveryFulfillmentTypes = (
  response: IAvailableFulfillmentOptionsResponse | null,
  fulfillmentGroup: IFulfillmentGroup,
) => {
  const fulfillmentOptions =
    response?.data?.availableFulfillmentOptions?.delivery?.fulfillmentOptions;
  const allowedFulfillmentTypes =
    getAllowedOosDeliveryFulfillmentTypes(fulfillmentGroup);

  return allowedFulfillmentTypes.filter((fulfillmentType) => {
    if (fulfillmentType === FULFILLMENT_TYPE.STH) {
      return fulfillmentOptions?.STH?.isEligible === true;
    }
    if (fulfillmentType === FULFILLMENT_TYPE.BODFS) {
      return fulfillmentOptions?.BODFS?.isEligible === true;
    }
    return false;
  });
};

export const getOosDeliveryDrawerOption = (
  fulfillmentGroup: IFulfillmentGroup,
): TOosRecoveryOption | '' => {
  if (canConsiderSthOosRecovery(fulfillmentGroup)) {
    return OOS_RECOVERY_OPTIONS.STH_DELIVERY_OPTION;
  }

  if (canConsiderBodfsOosRecovery(fulfillmentGroup)) {
    return OOS_RECOVERY_OPTIONS.BODFS_DELIVERY_OPTION;
  }

  return '';
};

export const getOosRecoveryDrawerOptions = (
  fulfillmentGroup: IFulfillmentGroup,
): TOosRecoveryOption[] => {
  const deliveryOosRecoveryOption =
    getOosDeliveryDrawerOption(fulfillmentGroup);

  return [
    ...(hasOosRecoveryOption(
      fulfillmentGroup,
      OOS_RECOVERY_OPTIONS.STORE_PICKUP_OPTION,
    )
      ? [OOS_RECOVERY_OPTIONS.STORE_PICKUP_OPTION]
      : []),
    ...(deliveryOosRecoveryOption ? [deliveryOosRecoveryOption] : []),
  ];
};

export const isOosDeliveryRecoveryOption = (option?: TOosRecoveryOption | '') =>
  option === OOS_RECOVERY_OPTIONS.STH_DELIVERY_OPTION ||
  option === OOS_RECOVERY_OPTIONS.BODFS_DELIVERY_OPTION;
